import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChange
} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable, of, Subject} from 'rxjs';
import {filter, switchMap, takeUntil, tap} from 'rxjs/operators';
import {TypedSimpleChanges} from '../../../common/typed-on-changes';
import {Side} from '../../../enums/side';
import {ExchangeAccountBase} from '../../../models/exchange-account';
import {Security} from '../../../models/security';
import {SecurityKey} from '../../../models/security-key';
import {ModalService} from '../../modal';
import {SecurityService} from '../../security';
import {SelectInputComponent} from '../../select-input';
import {SelectItem} from '../../select-input/select-item';
import {getDefaultLeveragesForSides} from '../leverage';
import {LeverageService} from '../leverage.service';

@Component({
    selector: 'leverage-select',
    templateUrl: '../../select-input/select-input.component.html',
    styleUrls: ['../../select-input/select-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LeverageSelectComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeverageSelectComponent extends SelectInputComponent implements OnInit, OnChanges {

    @Input()
    public securityKey: SecurityKey;

    @Input()
    public accountBase: ExchangeAccountBase;

    @Input()
    public side: Side;

    public override selectMenuItems: Array<SelectItem<number | undefined>> = [];

    private readonly updateMenuItems$ = new Subject<void>();

    constructor(
        elementRef: ElementRef,
        modalService: ModalService,
        injector: Injector,
        changeDetectorRef: ChangeDetectorRef,
        private securityService: SecurityService,
        private leverageService: LeverageService
    ) {
        super(
            elementRef,
            modalService,
            injector,
            changeDetectorRef
        );
    }

    public override ngOnInit(): void {
        super.ngOnInit();

        this.initMenuItemsUpdating();
    }

    public override ngOnChanges(changes: TypedSimpleChanges<LeverageSelectComponent>): void {
        super.ngOnChanges(changes);

        const doUpdateMenuItems: boolean = changes.securityKey && !changes.securityKey.firstChange
            || changes.accountBase && !changes.accountBase.firstChange
            || changes.side && !changes.side.firstChange;

        if (doUpdateMenuItems) {
            this.updateMenuItems$.next();
        }
    }

    private initMenuItemsUpdating(): void {
        const getSideLeverages = (security: Security): Observable<number[]> =>
            !security
                ? of(getDefaultLeveragesForSides().buyLeverages) // Default leverages, side doesn't matter.
                : this.leverageService.getSideLeveragesForSecurityAcc(
                    security,
                    this.accountBase,
                    this.side
                );

        this.updateMenuItems$.pipe(
            filter(() => !!this.securityKey),
            switchMap(() => this.securityService.getSecurityBySecurityKey(this.securityKey)),
            switchMap(security => getSideLeverages(security)),
            tap(sideLeverages => {
                const prevMenuItems = this.selectMenuItems;

                this.selectMenuItems = sideLeverages.map<SelectItem>(leverage => ({
                    value: leverage,
                    label: String(leverage)
                }));

                const changes: TypedSimpleChanges<SelectInputComponent> = {
                    selectMenuItems: new SimpleChange(prevMenuItems, this.selectMenuItems, false)
                };

                super.ngOnChanges(changes);
            }),
            takeUntil(this.destroy$)
        ).subscribe();

        this.updateMenuItems$.next(); // Do the initial update.
    }

}
